import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { initAppBrowser } from "@actions/initApp";
import styled from "@emotion/styled";
import { FiArrowDown } from "react-icons/fi";
import DefaultCustomButton from "@modules/Components/DefaultCustomButton";
import { ClearedStyleRouterLink } from "@modules/Components";

const HeaderMain = styled.div`
  border-radius: 0px 0px 460px 0px;
  background-image: radial-gradient(
      circle at 100% 0,
      rgba(255, 255, 255, 0.14),
      rgba(255, 255, 255, 0) 57%
    ),
    linear-gradient(to bottom, #271763, #271763);
  position: relative;
  text-align: center;

  @media (max-width: 1600px) {
    border-radius: 0px 0px 360px 0px;
  }
  @media (max-width: 1100px) {
    border-radius: 0px 0px 200px 0px;
  }
  @media (max-width: 999px) {
    overflow: hidden;
  }
  @media (max-width: 800px) {
    border-radius: 0px 0px 100px 0px;
  }
  @media (max-width: 700px) {
    padding-bottom: 180px;
  }

  @media (max-width: 400px) {
    border-radius: 0px 0px 90px 0px;
  }
`;

const AbsoluteImageLeft = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transform: rotateY(180deg);
  @media (max-width: 1275px) {
    width: 100px;
    height: auto;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const AbsoluteContainer = styled.div`
  position: absolute;
  bottom: -16px;
  right: 35px;

  @media (max-width: 1700px) {
    bottom: 0px;
    right: 92px;
  }
  @media (max-width: 1600px) {
    bottom: 0px;
    right: 28px;
  }
  @media (max-width: 999px) {
    bottom: -49px;
    right: -11px;
  }
  @media (max-width: 800px) {
    bottom: -78px;
    right: -42px;
  }
  @media (max-width: 700px) {
    bottom: -59px;
    right: 0px;
    margin: 0 auto;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const AbsoluteNumber = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 60px;
  line-height: 1.5;
  text-align: center;
  color: #fff;

  @media (max-width: 1700px) {
    font-size: 50px;
  }
`;

const BigNumberSpan = styled.span`
  font-size: 40px;
  opacity: 0.64;
  @media (max-width: 1700px) {
    font-size: 36px;
  }
`;

const Market = styled.div`
  opacity: 0.48;
  font-size: 16px;
  line-height: 1.88;
  color: #fff;
`;

const AbsoluteImageRight = styled.div`
  width: 419px;
  height: 419px;
  opacity: 0.2;
  border-radius: 50%;
  background-image: linear-gradient(
    134deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0) 70%,
    rgba(255, 255, 255, 0) 70%
  );

  @media (max-width: 1700px) {
    width: 300px;
    height: 300px;
  }
  @media (max-width: 700px) {
    width: 375px;
    height: 375px;
  }
`;

const Header = styled.h1`
  font-size: 72px;
  line-height: 1.25;
  text-align: center;
  color: #fff;
  max-width: 1187px;
  margin: 0 auto;
  padding-top: 100px;
  padding-bottom: 70px;

  @media (max-width: 1500px) {
    font-size: 66px;
  }
  @media (max-width: 1450px) {
    padding-bottom: 47px;
  }
  @media (max-width: 1200px) {
    font-size: 48px;
  }
  @media (max-width: 999px) {
    padding-top: 70px;
  }
  @media (max-width: 950px) {
    font-size: 40px;
  }
  @media (max-width: 728px) {
    max-width: 90%;
  }
  @media (max-width: 500px) {
    font-size: 36px;
    max-width: 300px;
  }
`;

const HeaderP = styled.div`
  opacity: 0.8;
  font-size: 18px;
  line-height: 2;
  text-align: center;
  color: #fff;
  max-width: 920px;
  margin: 0 auto;

  @media (max-width: 1450px) {
    max-width: 700px;
    font-size: 17px;
  }
  @media (max-width: 1200px) {
    max-width: 500px;
    font-size: 16px;
  }
  @media (max-width: 999px) {
    padding-bottom: 60px;
  }
  @media (max-width: 950px) {
    max-width: 345px;
  }
  @media (max-width: 450px) {
    max-width: 90%;
  }
`;

const CircleP = styled.div`
  width: 96px;
  height: 96px;
  box-shadow: 30px 30px 50px 0 rgba(31, 32, 33, 0.15);
  background-color: #ffffff;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  text-align: center;
  margin-top: 68px;
  cursor: pointer;
  bottom: -43px;

  @media (max-width: 1450px) {
    margin-top: 30px;
  }
  @media (max-width: 999px) {
    display: none;
  }
`;

const Arrow = styled(FiArrowDown)`
  color: #777984;
  font-size: 26px;
  opacity: 0.8;
  position: absolute;
  bottom: 35%;
  right: 35%;
`;

const TopSection = styled.section`
  max-width: 1255px;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  margin: 0 auto;
  grid-gap: 100px;
  margin-top: 180px;

  @media (max-width: 1830px) {
    max-width: 70%;
  }
  @media (max-width: 1600px) {
    max-width: 80%;
  }
  @media (max-width: 1350px) {
    grid-gap: 70px;
  }
  @media (max-width: 1300px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 800px) {
    margin-top: 80px;
  }
  @media (max-width: 600px) {
    max-width: 90%;
  }
`;

const TextSection = styled.div`
  font-size: 18px;
  line-height: 2;
  color: #777984;

  @media (max-width: 1300px) {
    font-size: 17px;
    text-align: center;
    grid-row: ${({ gridRow }) => gridRow};
  }
  @media (max-width: 515px) {
    font-size: 16px;
  }
`;
const StatSection = styled.div`
  position: relative;
  font-size: 16px;
  line-height: 1.88;
  text-align: center;
  color: #777984;
  border-radius: 0px 140px 0px 0px;
  background-color: rgba(34, 176, 252, 0.04);
  padding-top: ${({ paddingTop }) => paddingTop};
  margin-top: ${({ marginTop }) => marginTop};
  height: fit-content;
  padding-bottom: ${({ paddingBottom }) => paddingBottom};

  @media (max-width: 1300px) {
    padding-top: 40px;
    margin-top: 20px;
  }
  @media (max-width: 515px) {
    font-size: 15px;
  }
`;

const StatSectionInner = styled.div`
  margin: 0 auto;
  max-width: 482px;

  @media (max-width: 1500px) {
    max-width: 95%;
  }
  @media (max-width: 1300px) {
    max-width: 85%;
  }
  @media (max-width: 600px) {
    max-width: 90%;
  }
`;

const TextHeader = styled.h2`
  font-size: 36px;
  line-height: 1.44;
  color: #1f2021;
  margin-top: 36px;
  margin-bottom: 56px;

  @media (max-width: 1300px) {
    font-size: 30px;
  }
  @media (max-width: 800px) {
    margin-top: 24px;
    margin-bottom: 24px;
  }
  @media (max-width: 600px) {
    font-size: 24px;
    line-height: 1.5;
  }
  @media (max-width: 515px) {
    font-size: 22px;
  }
`;

const Text = styled.div`
  margin-bottom: 20px;
`;

const TextBold = styled.span`
  font-weight: 500;
  color: #1f2021;
`;

const TextItalic = styled.span`
  color: #1f2021;
  font-style: italic;
`;

const BlueLine = styled.div`
  height: 3px;
  background-color: #12b2ea;
  width: 36px;
  margin: ${props => props.margin};

  @media (max-width: 1300px) {
    margin: 0 auto;
  }
`;

const StatHeader = styled.div`
  font-size: 24px;
  font-weight: 500;
  text-align: ${({ textAlign }) => textAlign};
  color: #12b2ea;
  max-width: ${({ maxWidth }) => maxWidth};

  @media (max-width: 515px) {
    font-size: 22px;
    max-width: 300px;
    margin: 0 auto;
    text-align: center;
  }
  @media (max-width: 345px) {
    max-width: 220px;
  }
`;

const StatP = styled.div`
  margin-top: 32px;
  margin-bottom: 48px;
`;

const StatYearGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 24px;

  @media (max-width: 470px) {
    grid-template-columns: 1fr;
  }
`;

const Years = styled.div`
  display: grid;
  grid-row-gap: 8px;
`;

const AnnualGrowth = styled.div`
  width: 136px;
  height: 136px;
  border-radius: 50%;
  background-image: linear-gradient(
    to right,
    rgba(34, 176, 252, 0.16),
    rgba(34, 176, 252, 0.02)
  );
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 470px) {
    grid-row: 1/2;
    margin: 0 auto;
  }
`;

const AnnualGrowthHeader = styled.div`
  font-size: 24px;
  font-weight: 500;
  text-align: center;
  color: #12b2ea;

  @media (max-width: 515px) {
    font-size: 22px;
  }
`;

const AnnualGrowthHeaderSpan = styled.span`
  font-size: 16px;
  @media (max-width: 515px) {
    font-size: 15px;
  }
`;

const AnnualGrowthText = styled.div`
  font-size: 12px;
  line-height: 1.33;
  text-align: center;
  color: #0c789e;
  @media (max-width: 515px) {
    font-size: 11px;
  }
`;

const YearContainer = styled.div`
  display: flex;
  font-size: 16px;
  align-items: center;
  line-height: 1.5;
  padding-left: 33px;
  padding-right: 33px;
  background-image: ${({ bgImage }) => bgImage};
  border-radius: ${({ borderRadius }) => borderRadius};
  @media (max-width: 650px) {
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 15px;
  }
  @media (max-width: 515px) {
    font-size: 14px;
  }
`;

const TheYear = styled.div`
  color: ${({ theColor }) => theColor};
  justify-self: flex-start;
  text-align: left;

  @media (max-width: 470px) {
    text-align: center;
  }
`;

const TheAmount = styled.div`
  font-weight: bold;
  color: ${({ theColor }) => theColor};
  justify-self: flex-end;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  opacity: 0.15;
  background-image: linear-gradient(
    to right,
    rgba(66, 67, 69, 0.3),
    #1f2021 51%,
    rgba(31, 32, 33, 0.3)
  );
  margin-top: 48px;
  margin-bottom: 53px;
`;

const StatsSecondGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 20px;
  align-items: center;
  grid-row-gap: 32px;
`;

const CirclePercent = styled.div`
  width: 75px;
  height: 75px;
  border-radius: 50%;
  background-image: linear-gradient(
    to right,
    rgba(34, 176, 252, 0.16),
    rgba(34, 176, 252, 0.02)
  );
  font-size: 24px;
  font-weight: 500;
  text-align: center;
  color: #12b2ea;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 515px) {
    font-size: 22px;
  }
`;

const CirclePercentSpan = styled.span`
  font-size: 16px;
  @media (max-width: 515px) {
    font-size: 15px;
  }
`;

const SecondText = styled.div`
  font-size: 16px;
  line-height: 1.88;
  color: #777984;
  text-align: left;
  max-width: ${({ maxWidth }) => maxWidth};

  @media (max-width: 515px) {
    font-size: 15px;
  }
`;

const StatHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  text-align: left;
  margin-bottom: 48px;

  @media (max-width: 515px) {
    grid-template-columns: 1fr;
  }
`;

const StatHeaderImage = styled.img`
  height: 158px;
  width: 158px;

  @media (max-width: 515px) {
    justify-self: center;
    margin-top: 30px;
  }
`;

const PurchaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  line-height: 1.88;
  font-size: 16px;
  color: #777984;
  text-align: left;
  align-items: center;
  grid-column-gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 1450px) {
    font-size: 14px;
  }
  @media (max-width: 515px) {
    font-size: 13px;
  }
`;

const PurchaseAmount = styled.div`
  font-weight: bold;
  color: ${({ textColor }) => textColor};
`;

const PurchaseBox = styled.div`
  background-color: ${({ boxColor }) => boxColor};
  width: 16px;
  height: 16px;
  border-radius: 4px;
`;

const MerchantGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 24px;

  @media (max-width: 515px) {
    grid-template-columns: 1fr;
  }
`;

const SoldMore = styled.div`
  @media (max-width: 515px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;
  }
`;

const More = styled.div`
  border-radius: 0px 32px 0px 0px;
  background-image: linear-gradient(
    222deg,
    rgba(34, 176, 252, 0.16),
    rgba(34, 176, 252, 0.02)
  );
  font-size: 14px;
  text-align: center;
  color: #12b2ea;
  padding: 20px 34px;

  @media (max-width: 515px) {
    font-size: 13px;
  }
  @media (max-width: 360px) {
    padding: 20px 20px;
  }
  @media (max-width: 340px) {
    padding: 20px 10px;
  }
`;

const Less = styled.div`
  border-radius: 0px 0px 32px 0px;
  background-image: linear-gradient(
    318deg,
    rgba(255, 156, 27, 0.16),
    rgba(255, 156, 27, 0.02)
  );
  font-size: 14px;
  text-align: center;
  color: #ff9c1b;
  padding: 20px 34px;

  @media (max-width: 515px) {
    font-size: 13px;
  }
  @media (max-width: 360px) {
    padding: 20px 20px;
  }
  @media (max-width: 340px) {
    padding: 20px 10px;
  }
`;

const MoreHeader = styled.div`
  font-weight: 500;
`;

const MoreText = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: #0c789e;

  @media (max-width: 515px) {
    font-size: 11px;
  }
`;

const LessHeader = styled.div`
  font-weight: 500;
`;

const LessText = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: #cc7d16;

  @media (max-width: 515px) {
    font-size: 11px;
  }
`;

const Vs = styled.div`
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: center;
  color: #777984;
  margin-top: 3px;
  margin-bottom: 3px;

  @media (max-width: 515px) {
    font-size: 11px;
    grid-row: 2/3;
    grid-column: 1/3;
    margin-top: 20px;
  }
`;

const Merchant = styled.div`
  text-align: left;
  align-items: center;
  display: grid;
  margin-bottom: 10px;
  font-size: 12px;
  padding-right: 20px;
  padding-left: 20px;
  height: 48px;
  line-height: 2.5;
  grid-template-columns: 1fr auto;
  border-radius: ${({ borderRadius }) => borderRadius};
  background-image: ${({ bgColor }) =>
    bgColor === "blue"
      ? "linear-gradient(to right, rgba(34, 176, 252, 0.16), rgba(34, 176, 252, 0.02))"
      : "linear-gradient(to right, rgba(255, 156, 27, 0.16), rgba(255, 156, 27, 0.02))"};

  @media (max-width: 515px) {
    font-size: 11px;
  }
`;

const Name = styled.div`
  color: ${({ color }) => (color === "blue" ? "#0c789e" : "#cc7d16")};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const Percent = styled.div`
  font-weight: bold;
  color: ${({ color }) => (color === "blue" ? "#12b2ea" : "#ff9c1b")};
`;

const BottomImage = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;

  @media (max-width: 515px) {
    display: none;
  }
`;

const BottomSection = styled.div`
  position: relative;
  box-shadow: 0 20px 80px 0 rgba(119, 121, 132, 0.1);
  background-color: #fff;
  display: grid;
  grid-template-columns: 1fr auto;
  padding-right: 100px;
  padding-left: 100px;
  height: 208px;
  align-items: center;
  bottom: -109px;

  @media (max-width: 1400px) {
    padding-right: 60px;
    padding-left: 60px;
  }
  @media (max-width: 950px) {
    display: block;
    text-align: center;
    height: auto;
    padding-top: 40px;
    padding-bottom: 60px;
  }
  @media (max-width: 768px) {
    padding-right: 10%;
    padding-left: 10%;
  }
`;

const BottomText = styled.div`
  font-size: 24px;
  line-height: 1.5;
  color: #1f2021;

  @media (max-width: 950px) {
    margin-bottom: 40px;
    margin-top: 40px;
  }
  @media (max-width: 515px) {
    font-size: 22px;
  }
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 542px;
  grid-gap: 38px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-width: auto;
  }
`;

const BottomTextBold = styled.span`
  font-weight: 500;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 30px;
  width: 100%;
  padding-left: 7px;
  align-items: center;

  @media (max-width: 950px) {
    grid-gap: 0px;
  }
`;

const ButtonCircle = styled.div`
  background-color: rgba(38, 38, 38, 0.15);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonText = styled.div`
  @media (max-width: 950px) {
    padding-right: 15%;
  }
  @media (max-width: 350px) {
    padding-right: 5%;
  }
  @media (max-width: 300px) {
    padding-right: 0%;
  }
`;
const purchasingArray = [
  {
    text: "Gift cards allow the recipient to select",
    amount: "50.4",
    textColor: "#12b2ea",
    boxColor: "#12b2ea"
  },
  {
    text: "Gift cards are easier and faster to buy",
    amount: "24.7",
    textColor: "#ff9c1b",
    boxColor: "#ff9c1b"
  },
  {
    text: "Recipient does not have to worry about returning",
    amount: "5.6",
    textColor: "#777984",
    boxColor: "#ffbb64"
  },
  {
    text: "It is easier to mail a gift card than a gift",
    amount: "5.6",
    textColor: "#777984",
    boxColor: "#ffd39a"
  },
  {
    text: "Giving gift cards helps me stick to holiday budget",
    amount: "5.2",
    textColor: "#777984",
    boxColor: "#5ecaf0"
  },
  {
    text: "Incentives for buying gift cards",
    amount: "4.4",
    textColor: "#777984",
    boxColor: "#83d6f4"
  },
  {
    text: "None of the above",
    amount: "4.1",
    textColor: "#777984",
    boxColor: "#a0e0f6"
  }
];

const GiftCardStatistics = () => {
  const dispatch = useDispatch();
  const myRef = useRef(null);
  const largestDiscountMerchants = useSelector(
    state => state.merchantsBuy.largestDiscountMerchants
  );

  useEffect(() => {
    dispatch(initAppBrowser(["messages", "statistics"]));
    window.scrollTo(0, 0);
  }, []);

  const handleCircleClick = () => {
    window.scrollTo(0, myRef.current.offsetTop);
  };

  return (
    <div className="mar-t-128">
      <Helmet title={`Gift Card Industry Statistics from CardCash`} />
      <HeaderMain>
        <AbsoluteImageLeft
          src="https://cdn.cardcash.com/website/ui/howiw/top-right-elements.svg"
          alt=""
        />
        <AbsoluteContainer>
          <AbsoluteImageRight />
          <AbsoluteNumber>
            <div>
              <BigNumberSpan>$</BigNumberSpan>534B
            </div>
            <Market>market size</Market>
          </AbsoluteNumber>
        </AbsoluteContainer>

        <Header>Gift Card Statistics</Header>
        <HeaderP>
          Gift cards are a multi billion dollar industry and it's only growing!
          CardCash took a consumer survey and compiled the most up to date
          statistics so you can have a clear picture of what's really going on.
        </HeaderP>
        <CircleP onClick={handleCircleClick} ref={myRef}>
          <Arrow className="arrow" />
        </CircleP>
      </HeaderMain>

      <main>
        <TopSection>
          <TextSection gridRow={"1/2"}>
            <BlueLine />
            <TextHeader>Digital Gift Cards</TextHeader>
            <Text>
              The global Gift Card market size is projected to reach $584
              billion by 2026.{" "}
              <TextBold>
                While some are even projected it to hit $1.8 Trillion by 2030!
              </TextBold>
            </Text>
            <Text>
              While physical gift cards are growing at an annual rate of 9%,
              digital gift cards are projected to grow at an annual rate of more
              than 26% by 2025.
            </Text>
            <Text>
              Research finds that since the beginning of the pandemic, 32% of
              consumers have either purchased a gift card online for the first
              time, or with greater frequency than previously.
            </Text>
            <Text>
              In a CardCash survey over fifty percent of respondents said they
              would prefer to have a digital gift card scanned from their phone
              than carry an email printout. Also, 53% said they would be
              interested in storing gift cards on their phones.
            </Text>
            <Text>
              85% of gift cards that are sold on the CardCash website are
              digital e-gift cards.
            </Text>
            <Text>
              71% of consumers that were surveyed report they are interested in
              digital gift cards vs. other gifts.
            </Text>
            <Text>
              <TextBold>
                CardCash has increased the amount of gift cards that get sold as
                ecards, and now accepts over 500 e-gift card merchants!
              </TextBold>
            </Text>
          </TextSection>
          <StatSection
            paddingTop={"80px"}
            marginTop={"100px"}
            paddingBottom={"64px"}
          >
            <StatSectionInner>
              <StatHeader textAlign={"center"}>
                Gift Card Market Totals $534 Billion
              </StatHeader>
              <StatP>
                E-gifting is the fastest growing segment and are projected to
                grow more than 26% by 2025.
              </StatP>
              <StatYearGrid>
                <Years>
                  <YearContainer
                    bgImage={
                      "linear-gradient(to right, rgba(34, 176, 252, 0.16), rgba(34, 176, 252, 0.02))"
                    }
                    borderRadius={"32px 0px 0px 32px"}
                  >
                    <TheYear theColor={"#0c789e"}>
                      Over 35 million US citizens said in a recent survey, they
                      would prefer to receive a gift card rather than an actual
                      gift.
                    </TheYear>
                  </YearContainer>
                </Years>
                <AnnualGrowth>
                  <AnnualGrowthHeader>
                    <div>
                      26<AnnualGrowthHeaderSpan>%</AnnualGrowthHeaderSpan>
                      <AnnualGrowthText>
                        Expected annual growth
                      </AnnualGrowthText>
                    </div>
                  </AnnualGrowthHeader>
                </AnnualGrowth>
              </StatYearGrid>
              <Divider />
              <StatsSecondGrid>
                <CirclePercent>3B</CirclePercent>
                <SecondText>
                  $3 billion in unused gift cards goes to waste every single
                  year.
                </SecondText>
                <CirclePercent>
                  53<CirclePercentSpan>%</CirclePercentSpan>
                </CirclePercent>
                <SecondText>
                  Over 50% of respondents would prefer to have a digital gift
                  card scanned from their phone.
                </SecondText>
                <CirclePercent>
                  85<CirclePercentSpan>%</CirclePercentSpan>
                </CirclePercent>
                <SecondText maxWidth={"291px"}>
                  Over 85% of gift cards that are sold on the CardCash website
                  are digital e-gift cards.
                </SecondText>
              </StatsSecondGrid>
            </StatSectionInner>
            <BottomImage
              src="https://cdn.cardcash.com/website/ui/gift-card-stats/PhoneIcon.svg"
              alt=""
            />
          </StatSection>
          <StatSection
            paddingTop={"40px"}
            marginTop={"20px"}
            paddingBottom={"40px"}
          >
            <StatSectionInner>
              <StatHeaderGrid>
                <StatHeader textAlign={"left"} maxWidth={"260px"}>
                  Why are consumers purchasing gift cards?
                </StatHeader>
                <StatHeaderImage
                  src="https://cdn.cardcash.com/website/ui/gift-card-stats/Chart@1x.png"
                  alt=""
                />
              </StatHeaderGrid>

              {purchasingArray.map((item, index) => (
                <PurchaseGrid key={index}>
                  <div>{item.text}</div>
                  <PurchaseAmount textColor={item.textColor}>
                    {item.amount}%
                  </PurchaseAmount>
                  <PurchaseBox boxColor={item.boxColor} />
                </PurchaseGrid>
              ))}
              <Divider />
              <StatHeader textAlign={"center"}>CardCash Stats</StatHeader>
              <StatP>Merchants with the highest discount</StatP>
              <MerchantGrid>
                <div>
                  {largestDiscountMerchants.map((item, index) => (
                    <Merchant
                      key={index}
                      bgColor={index % 2 === 0 ? "blue" : "orange"}
                      borderRadius={
                        index === 0
                          ? "32px 0px 0px 0px"
                          : index === 5
                          ? "0px 0px 0px 32px"
                          : ""
                      }
                    >
                      <Name color={index % 2 === 0 ? "blue" : "orange"}>
                        {item.name}
                      </Name>
                      <Percent color={index % 2 === 0 ? "blue" : "orange"}>
                        {Math.floor(item.upToPercentage)}%
                      </Percent>
                    </Merchant>
                  ))}
                </div>
                <SoldMore>
                  <More>
                    <MoreHeader>Sold more</MoreHeader>
                    <MoreText>Home Depot</MoreText>
                    <MoreText>Best Buy</MoreText>
                    <MoreText>Starbucks</MoreText>
                    <MoreText>Sam’s Club</MoreText>
                    <MoreText>Dick’s Sporting Goods</MoreText>
                  </More>
                  <Vs>
                    {new Date().getFullYear() - 1} vs {new Date().getFullYear()}
                  </Vs>
                  <Less>
                    <LessHeader>Sold less</LessHeader>
                    <LessText>Lowes</LessText>
                    <LessText>TJ Maxx</LessText>
                    <LessText>iTunes</LessText>
                    <LessText>CVS</LessText>
                    <LessText>Macys</LessText>
                  </Less>
                </SoldMore>
              </MerchantGrid>
            </StatSectionInner>
          </StatSection>
          <TextSection gridRow={"3/4"}>
            <BlueLine />
            <TextHeader>Gift Card Purchases</TextHeader>
            <Text>
              <TextBold>
                73% of consumers said that they will be purchasing a gift card
                this holiday season.
              </TextBold>
            </Text>
            <Text>
              On average most consumers purchase 2 gift cards during the holiday
              season, followed closely by 3 gift cards.
            </Text>

            <Text>
              When asked if they would purchase gift cards as part of their
              online shopping for the holidays, 29.6 percent were either ‘very’
              or ‘extremely likely’ to do so.
            </Text>

            <Text>
              Millennials and Gen Xers led the way, with a 35.7 and 34.7 percent
              likelihood to purchase gift cards, respectively.
            </Text>

            <Text>
              <TextBold>Why are people buying gift cards? </TextBold> 50% of
              consumers like allowing the recipient to purchase their own gift.
              Almost 25% of consumers say that gift cards are easier to buy.
            </Text>
            <Text>
              The higher your income, the more likely you are to have purchased
              or received a digital gift card.
            </Text>
            <Text>
              62% of 18- to 34-year-olds say they are interested in receiving
              gift cards through social networks or messaging apps.
            </Text>
            <Text>
              Where you live can make a big difference in whether you have
              purchased or received a digital gift card. Midwesterners are less
              likely to have purchased a digital gift card than their eastern
              and western counterparts.
            </Text>

            <Text>
              <TextBold>Best Selling Gift Cards at Card Cash: </TextBold>
              <TextItalic>
                Walmart, Target, Home Depot, Best Buy, Lowes, Foot Locker, Nike,
                Starbucks, Nordstrom, Bed Bath & Beyond, Sam’s Club, Dick’s
                Sporting Goods, Sephora, Apple, Ulta and Kohls.
              </TextItalic>
            </Text>
          </TextSection>
        </TopSection>
        <BottomSection className="main-container-page-width">
          <BottomText>
            Are you ready to <BottomTextBold>start saving?</BottomTextBold>
          </BottomText>
          <ButtonContainer>
            <ClearedStyleRouterLink to="/buy-gift-cards/">
              <DefaultCustomButton
                buttonType={"ORANGE_BUTTON"}
                handleClick={f => f}
                disabled={false}
                classes={"main-default-orange-button"}
              >
                <ButtonGrid>
                  <ButtonCircle bgColor={"rgba(38, 38, 38, 0.15)"}>
                    <img
                      src="https://cdn.cardcash.com/website/ui/gift-card-stats/PriceTags.svg"
                      alt=""
                    />
                  </ButtonCircle>
                  <ButtonText>BUY & SAVE</ButtonText>
                </ButtonGrid>
              </DefaultCustomButton>
            </ClearedStyleRouterLink>
            <ClearedStyleRouterLink to="/sell-gift-cards/">
              <DefaultCustomButton
                buttonType={"GREEN_BUTTON"}
                handleClick={f => f}
                disabled={false}
                classes={"main-default-orange-button"}
              >
                <ButtonGrid>
                  <ButtonCircle bgColor={"rgba(38, 38, 38, 0.15)"}>
                    <img
                      src="https://cdn.cardcash.com/website/ui/gift-card-stats/money-bills.svg"
                      alt=""
                    />
                  </ButtonCircle>
                  <ButtonText>SELL & EARN</ButtonText>
                </ButtonGrid>
              </DefaultCustomButton>
            </ClearedStyleRouterLink>
          </ButtonContainer>
        </BottomSection>
      </main>
    </div>
  );
};

export default GiftCardStatistics;
